import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Label,
  Modal,
  Select,
  Table,
  Textarea,
  TextInput,
} from "flowbite-react";
import {
  HiChevronDown,
  HiChevronUp,
  HiOutlinePlus,
  HiPencil,
  HiTrash,
} from "react-icons/hi";
import AnimatedToast from "../components/common/AnimatedToast";
import CorporateBreadcrumb from "../components/common/CorporateBreadcrumb";
import axiosInstance from "../config/axiosInstance";
import { LEADS } from "../config/apiConfig";

const FIELD_TYPES = [
  { value: "text", label: "Texto (curto)" },
  { value: "textarea", label: "Texto longo" },
  { value: "number", label: "Número" },
  { value: "currency", label: "Moeda" },
  { value: "percent", label: "Percentual" },
  { value: "date", label: "Data" },
  { value: "datetime", label: "Data e hora" },
  { value: "select", label: "Seleção única" },
  { value: "radio", label: "Botões de opção" },
  { value: "checkbox", label: "Checkbox (valor booleano)" },
  { value: "boolean", label: "Toggle (booleano)" },
];

const endpoint = LEADS.CUSTOM_FIELDS;
const fieldItem = (id) => LEADS.CUSTOM_FIELD(id);

const skeletonRows = Array.from({ length: 4 });
const mobileSkeletonRows = Array.from({ length: 3 });

const emptyForm = {
  id: null,
  label: "",
  key: "",
  type: "text",
  isRequired: false,
  options: "",
  placeholder: "",
  order: "",
};

const LeadCustomFieldsPage = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [animatedToast, setAnimatedToast] = useState(null);
  const [fieldMenuOpen, setFieldMenuOpen] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [menuSlideIn, setMenuSlideIn] = useState(false);
  const breadcrumbItems = useMemo(
    () => [
      { label: "Contatos", href: "/contacts" },
      { label: "Campos customizados", current: true },
    ],
    []
  );

  const fetchFields = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(endpoint);
      setFields(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      console.error("LeadCustomFieldsPage: fetch", error);
      setAnimatedToast({
        id: Date.now(),
        title: "Erro",
        message:
          error?.response?.data?.message ||
          "Não foi possível carregar os campos.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFields();
  }, []);

  const openModal = (field = null) => {
    if (field) {
      setFormData({
        id: field.id,
        label: field.label,
        key: field.key,
        type: field.type,
        isRequired: !!field.isRequired,
        options: (field.options || []).join("\n"),
        placeholder: field.placeholder || "",
        order: field.order ?? "",
      });
    } else {
      setFormData(emptyForm);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormData(emptyForm);
  };

  const isOptionType = useMemo(
    () => ["select", "radio"].includes(formData.type),
    [formData.type]
  );

  const handleSubmit = async (event) => {
    event?.preventDefault();
    setSaving(true);
    const payload = {
      label: formData.label.trim(),
      key: formData.key.trim(),
      type: formData.type,
      isRequired: formData.isRequired,
      options: isOptionType
        ? formData.options
            .split("\n")
            .map((item) => item.trim())
            .filter(Boolean)
            .join(",")
        : null,
      placeholder: formData.placeholder.trim() || null,
      order: formData.order ? Number(formData.order) : 0,
    };

    try {
      if (formData.id) {
        await axiosInstance.put(fieldItem(formData.id), payload);
        setAnimatedToast({
          id: Date.now(),
          title: "Sucesso",
          message: "Campo atualizado.",
        });
      } else {
        await axiosInstance.post(endpoint, payload);
        setAnimatedToast({
          id: Date.now(),
          title: "Sucesso",
          message: "Campo criado.",
        });
      }
      closeModal();
      fetchFields();
    } catch (error) {
      console.error("LeadCustomFieldsPage: save", error);
      setAnimatedToast({
        id: Date.now(),
        title: "Erro",
        message:
          error?.response?.data?.message || "Não foi possível salvar o campo.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm("Remover este campo customizado?")) return;
    try {
      await axiosInstance.delete(fieldItem(fieldId));
      setAnimatedToast({
        id: Date.now(),
        title: "Sucesso",
        message: "Campo removido.",
      });
      fetchFields();
    } catch (error) {
      console.error("LeadCustomFieldsPage: delete", error);
      setAnimatedToast({
        id: Date.now(),
        title: "Erro",
        message:
          error?.response?.data?.message || "Não foi possível remover o campo.",
      });
    }
  };

  const handleReorder = async (fieldId, direction) => {
    if (reordering || loading) return;
    const currentIndex = fields.findIndex((field) => field.id === fieldId);
    if (currentIndex === -1) return;
    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= fields.length) return;

    const updatedOrder = [...fields];
    const [movedField] = updatedOrder.splice(currentIndex, 1);
    updatedOrder.splice(targetIndex, 0, movedField);
    const normalized = updatedOrder.map((field, index) => ({
      ...field,
      order: index + 1,
    }));
    setFields(normalized);
    setReordering(true);
    try {
      await axiosInstance.post(LEADS.CUSTOM_FIELDS_REORDER, {
        orderedIds: normalized.map((field) => field.id),
      });
      setAnimatedToast({
        id: Date.now(),
        title: "Sucesso",
        message: "Ordem atualizada.",
      });
    } catch (error) {
      console.error("LeadCustomFieldsPage: reorder", error);
      setAnimatedToast({
        id: Date.now(),
        title: "Erro",
        message:
          error?.response?.data?.message ||
          "Não foi possível atualizar a ordem dos campos.",
      });
      await fetchFields();
    } finally {
      setReordering(false);
    }
  };

  useEffect(() => {
    if (!fieldMenuOpen) {
      setMenuSlideIn(false);
      return;
    }
    requestAnimationFrame(() => {
      setMenuSlideIn(true);
    });
  }, [fieldMenuOpen]);

  const openFieldMenu = (field) => {
    setActiveField(field);
    setFieldMenuOpen(true);
  };

  const closeFieldMenu = () => {
    setFieldMenuOpen(false);
    setActiveField(null);
  };

  const handleFieldEdit = () => {
    if (!activeField) return;
    openModal(activeField);
    closeFieldMenu();
  };

  const handleFieldDelete = async () => {
    if (!activeField) return;
    closeFieldMenu();
    await handleDelete(activeField.id);
  };

  return (
    <div className="space-y-4">
      <CorporateBreadcrumb items={breadcrumbItems} />
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
            Contatos
          </p>
          <h1 className="text-2xl font-semibold">Campos customizados</h1>
        </div>
        <Button className="hidden md:flex" onClick={() => openModal()}>
          <div className="flex items-center gap-2">
            <HiOutlinePlus />
            <span> Novo campo</span>
          </div>
        </Button>
      </div>

      <div className="sm:hidden fixed bottom-4 right-4 z-40">
        <Button
          className="h-14 w-14 rounded-full p-0 flex items-center justify-center shadow-xl"
          onClick={() => openModal()}
          aria-label="Novo campo customizado"
        >
          <HiOutlinePlus className="text-xl" />
        </Button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="hidden sm:block">
          {loading ? (
            <div className="overflow-x-auto">
              <Table hoverable>
                <Table.Head className="text-xs uppercase tracking-[0.3em] text-slate-500">
                  <Table.HeadCell>Ordem</Table.HeadCell>
                  <Table.HeadCell className="text-center">Nome</Table.HeadCell>
                  <Table.HeadCell>Chave</Table.HeadCell>
                  <Table.HeadCell>Tipo</Table.HeadCell>
                  <Table.HeadCell className="text-center">
                    Obrigatório
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center">Ações</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {skeletonRows.map((_, index) => (
                    <Table.Row key={`skeleton-${index}`}>
                      <Table.Cell>
                        <span className="h-4 w-32 rounded-full bg-gray-200 animate-pulse block" />
                      </Table.Cell>
                      <Table.Cell>
                        <span className="h-4 w-24 rounded-full bg-gray-200 animate-pulse block mx-auto" />
                      </Table.Cell>
                      <Table.Cell>
                        <span className="h-4 w-20 rounded-full bg-gray-200 animate-pulse block" />
                      </Table.Cell>
                      <Table.Cell>
                        <span className="h-4 w-10 rounded-full bg-gray-200 animate-pulse block" />
                      </Table.Cell>
                      <Table.Cell className="text-center">
                        <span className="h-4 w-8 rounded-full bg-gray-200 animate-pulse block mx-auto" />
                      </Table.Cell>
                      <Table.Cell className="text-center">
                        <span className="h-4 w-20 rounded-full bg-gray-200 animate-pulse block mx-auto" />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          ) : fields.length ? (
            <div className="overflow-x-auto">
              <Table hoverable>
                <Table.Head className="text-xs uppercase tracking-[0.3em] text-slate-500">
                  <Table.HeadCell>Ordem</Table.HeadCell>
                  <Table.HeadCell>Nome</Table.HeadCell>
                  <Table.HeadCell>Chave</Table.HeadCell>
                  <Table.HeadCell>Tipo</Table.HeadCell>
                  <Table.HeadCell className="text-center">
                    Obrigatório
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center">Ações</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {fields.map((field, index) => (
                    <Table.Row key={field.id}>
                      <Table.Cell>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1.5">
                            <Button
                              size="xs"
                              color="light"
                              onClick={() => handleReorder(field.id, "up")}
                              disabled={reordering || loading || index === 0}
                              aria-label="Mover para cima"
                            >
                              <HiChevronUp className="text-xs" />
                            </Button>
                            <Button
                              size="xs"
                              color="light"
                              onClick={() => handleReorder(field.id, "down")}
                              disabled={
                                reordering ||
                                loading ||
                                index === fields.length - 1
                              }
                              aria-label="Mover para baixo"
                            >
                              <HiChevronDown className="text-xs" />
                            </Button>
                          </div>
                          <span className="font-semibold text-slate-900">
                            {field.order}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="font-semibold text-slate-900">
                        {field.label}
                      </Table.Cell>
                      <Table.Cell>{field.key}</Table.Cell>
                      <Table.Cell>{field.type}</Table.Cell>
                      <Table.Cell className="text-center">
                        {field.isRequired ? "Sim" : "Não"}
                      </Table.Cell>
                      <Table.Cell className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => openModal(field)}
                            className="flex items-center gap-1"
                          >
                            <HiPencil />
                          </Button>
                          <Button
                            size="xs"
                            color="failure"
                            onClick={() => handleDelete(field.id)}
                            className="flex items-center gap-1"
                          >
                            <HiTrash />
                          </Button>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          ) : (
            <div className="text-sm text-slate-500 py-10 text-center">
              Nenhum campo customizado ainda.
            </div>
          )}
        </div>
        <div className="sm:hidden space-y-3">
          {loading ? (
            mobileSkeletonRows.map((_, index) => (
              <article
                key={`mobile-skeleton-${index}`}
                className="rounded-2xl border border-slate-200 bg-slate-100 p-4 shadow-inner"
              >
                <div className="space-y-2">
                  <span className="h-4 w-36 rounded-full bg-slate-200 animate-pulse block" />
                  <span className="h-3 w-24 rounded-full bg-slate-200 animate-pulse block" />
                  <span className="h-3 w-20 rounded-full bg-slate-200 animate-pulse block" />
                </div>
              </article>
            ))
          ) : fields.length ? (
            fields.map((field, index) => (
              <article
                key={field.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm cursor-pointer"
                onClick={() => openFieldMenu(field)}
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {field.label}
                    </p>
                    <p className="text-xs text-slate-500">
                      {field.type?.toUpperCase() || "TEXTO"}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      size="xs"
                      color="light"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleReorder(field.id, "up");
                      }}
                      disabled={reordering || loading || index === 0}
                      className="p-1"
                      aria-label="Mover para cima"
                    >
                      <HiChevronUp className="text-base" />
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleReorder(field.id, "down");
                      }}
                      disabled={
                        reordering || loading || index === fields.length - 1
                      }
                      className="p-1"
                      aria-label="Mover para baixo"
                    >
                      <HiChevronDown className="text-base" />
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>
                    <span className="font-semibold text-slate-800">Chave:</span>{" "}
                    {field.key}
                  </p>
                  <p>
                    <span className="font-semibold text-slate-800">
                      Obrigatório:
                    </span>{" "}
                    {field.isRequired ? "Sim" : "Não"}
                  </p>
                  <p>
                    <span className="font-semibold text-slate-800">Ordem:</span>{" "}
                    {field.order}
                  </p>
                </div>
              </article>
            ))
          ) : (
            <div className="text-sm text-slate-500 py-10 text-center">
              Nenhum campo customizado ainda.
            </div>
          )}
        </div>
      </div>

      <Modal show={modalOpen} onClose={closeModal} size="2xl">
        <Modal.Header>
          <div className="px-2 text-base mt-1">
            {formData.id ? "Editar campo" : "Novo campo"}
          </div>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="label" value="Nome do campo" />
                <TextInput
                  id="label"
                  required
                  value={formData.label}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      label: event.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="key" value="Chave" />
                <TextInput
                  id="key"
                  value={formData.key}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      key: event.target.value,
                    }))
                  }
                  placeholder="ex: referencia_trafego"
                />
              </div>
              <div>
                <Label htmlFor="type" value="Tipo" />
                <Select
                  id="type"
                  value={formData.type}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      type: event.target.value,
                    }))
                  }
                >
                  {FIELD_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input
                  id="isRequired"
                  type="checkbox"
                  checked={formData.isRequired}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      isRequired: event.target.checked,
                    }))
                  }
                  className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-0"
                />
                <Label htmlFor="isRequired">É Obrigatório?</Label>
              </div>
            </div>

            {isOptionType && (
              <div>
                <Label htmlFor="options" value="Opções (uma por linha)" />
                <Textarea
                  id="options"
                  rows={3}
                  value={formData.options}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      options: event.target.value,
                    }))
                  }
                />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="flex justify-end items-center">
            <div className="flex gap-2">
              <Button color="light" onClick={closeModal}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Salvando..." : "Salvar campo"}
              </Button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>

      {fieldMenuOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black opacity-40"
            onClick={closeFieldMenu}
          />
          <div
            className={`relative w-full rounded-t-xl bg-white px-6 pt-3 pb-8 shadow-[0_0_24px_rgba(15,23,42,0.25)] transition-transform duration-300 ease-out transform ${
              menuSlideIn ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-slate-200"></div>
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                Ações rápidas
              </p>
              <div>
                <p className="text-lg font-semibold text-slate-900">
                  {activeField?.label || "Campo"}
                </p>
                <p className="text-sm text-slate-500">
                  {activeField?.key || ""}
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  color="success"
                  fullWidth
                  className="rounded-2xl py-3 text-base font-semibold w-full"
                  onClick={handleFieldEdit}
                >
                  Editar campo
                </Button>
                <Button
                  color="failure"
                  fullWidth
                  className="rounded-2xl py-3 text-base font-semibold w-full"
                  onClick={handleFieldDelete}
                >
                  Remover campo
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AnimatedToast
        open={Boolean(animatedToast)}
        message={animatedToast?.message}
        title={animatedToast?.title}
        onClose={() => setAnimatedToast(null)}
      />
    </div>
  );
};

export default LeadCustomFieldsPage;
